import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "../style/About.css";
import Footer from "../components/Footer";
import Header from "../components/Header";

const About = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll(".ab-reveal").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const whyItems = [
    { num: "01", title: t("about.fairPricesTitle"),     text: t("about.fairPricesText") },
    { num: "02", title: t("about.serviceTitle"),        text: t("about.serviceText") },
    { num: "03", title: t("about.maintainedTitle"),     text: t("about.maintainedText") },
    { num: "04", title: t("about.communicationTitle"),  text: t("about.communicationText") },
    { num: "05", title: t("about.simpleProcessTitle"),  text: t("about.simpleProcessText") },
    { num: "06", title: t("about.qualityVehiclesTitle"),text: t("about.qualityVehiclesText") },
  ];

  return (
    <main className="ab-root">
      <Header />

      {/* ── HERO ───────────────────────────────────────── */}
      <section className="ab-hero">
        <div className="ab-hero__content">
          <div className="ab-hero__accent" />
          <span className="ab-eyebrow">{t("about.heroEyebrow")}</span>
          <h1 className="ab-hero__title">{t("about.heroTitle")}</h1>
          <p className="ab-hero__para">{t("about.heroTextOne")}</p>
          <div className="ab-hero__actions">
            <button
              type="button"
              className="ab-btn ab-btn--gold"
              onClick={() => navigate("/cars")}
            >
              {t("about.exploreCars")}
            </button>
            <button
              type="button"
              className="ab-btn ab-btn--outline"
              onClick={() => navigate("/contact")}
            >
              {t("about.contactUs")}
            </button>
          </div>
        </div>

        <div className="ab-hero__scroll">
          <span className="ab-hero__scroll-line" />
        </div>
      </section>

      {/* ── STORY ──────────────────────────────────────── */}
      <section className="ab-story">
        <div className="ab-story__text ab-reveal">
          <div className="ab-section-head">
            <span className="ab-eyebrow ab-eyebrow--dark">{t("about.aboutEyebrow")}</span>
            <h2 className="ab-section-title">{t("about.aboutTitle")}</h2>
          </div>
          <p className="ab-section-sub">{t("about.aboutText")}</p>
          <button
            type="button"
            className="ab-btn ab-btn--gold"
            onClick={() => navigate("/contact")}
          >
            {t("about.contactUs")}
          </button>
        </div>

        <div className="ab-story__image ab-reveal" style={{ transitionDelay: "0.14s" }}>
          <img
            src="https://images.unsplash.com/photo-1533473359331-0135ef1b58bf?auto=format&fit=crop&w=1200&q=80"
            alt={t("about.aboutTitle")}
          />
        </div>
      </section>

      {/* ── PURCHASE ───────────────────────────────────── */}
      <section className="ab-purchase">
        <div className="ab-purchase__image ab-reveal">
          <img
            src="https://images.unsplash.com/photo-1551830820-330a71b99659?auto=format&fit=crop&w=1200&q=80"
            alt={t("about.purchaseTitle")}
          />
        </div>

        <div className="ab-purchase__content ab-reveal" style={{ transitionDelay: "0.14s" }}>
          <div className="ab-section-head">
            <span className="ab-eyebrow ab-eyebrow--dark">{t("about.purchaseEyebrow")}</span>
            <h2 className="ab-section-title">{t("about.purchaseTitle")}</h2>
          </div>
          <p className="ab-section-sub">{t("about.purchaseText")}</p>
          <ul className="ab-purchase__list">
            <li>{t("about.fairValuation")}</li>
            <li>{t("about.fastProcess")}</li>
            <li>{t("about.quickResponse")}</li>
          </ul>
          <button
            type="button"
            className="ab-btn ab-btn--gold"
            onClick={() => navigate("/contact")}
          >
            {t("about.getOffer")}
          </button>
        </div>
      </section>

      {/* ── WHY CHOOSE ─────────────────────────────────── */}
      <section className="ab-why">
        <div className="ab-why__head ab-reveal">
          <div className="ab-section-head ab-section-head--light">
            <span className="ab-eyebrow">{t("about.whyEyebrow")}</span>
            <h2 className="ab-section-title ab-section-title--light">
              {t("about.whyTitle")}
            </h2>
          </div>
        </div>

        <div className="ab-why__grid">
          {whyItems.map((item, i) => (
            <div
              className="ab-why__card ab-reveal"
              key={item.num}
              style={{ transitionDelay: `${i * 0.09}s` }}
            >
              <span className="ab-why__num">{item.num}</span>
              <h3 className="ab-why__card-title">{item.title}</h3>
              <p className="ab-why__card-text">{item.text}</p>
            </div>
          ))}
        </div>

        <div className="ab-why__foot ab-reveal">
          <button
            type="button"
            className="ab-btn ab-btn--gold"
            onClick={() => navigate("/contact")}
          >
            {t("about.contactUs")}
          </button>
        </div>
      </section>

      {/* ── CONTACT CTA ────────────────────────────────── */}
      <section className="ab-cta">
        <div className="ab-cta__inner ab-reveal">
          <div className="ab-cta__text">
            <span className="ab-eyebrow">{t("about.contactEyebrow")}</span>
            <h2 className="ab-cta__title">{t("about.contactTitle")}</h2>
            <p className="ab-cta__sub">{t("about.contactText")}</p>
          </div>
          <button
            type="button"
            className="ab-btn ab-btn--gold ab-cta__btn"
            onClick={() => navigate("/contact")}
          >
            {t("about.contactUs")}
          </button>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default About;
